/**
 * Manages CPT table state: row data, dirty tracking, UserStore persistence,
 * and row-level update handling (status modals, hold modals, field updates).
 *
 * @module useCptTableState
 */
import { SearchableCardOption } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FilingInfoSavePayload } from "../../../../../components/modals/FilingInfoModal/filingInfoModal.utils";
import { useBusniessOfficeNotesContext } from "../../../../../context/busniessOfficeNotesContext";
import { useDataAccumulatorForSingleOrder } from "../../../../../context/dataAccumulatorForSingleOrder";
import { AuthStatusOptionModel } from "../../../../../data-model/authStatusOptions";
import {
  CptCodesTableRowModel,
  PaRequired,
} from "../../../../../data-model/cptCodesTableModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { ModalId } from "../../../../../enums/modalId";
import { CptCodeWithDetailsKeys } from "../../../../../enums/tableColumnKeys";
import { useFacilityCategory } from "../../../../../hooks/useOncoemrTasksEnabled";
import { setOpenedModalId } from "../../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { buildFilterJCodesFromTableData } from "../utils/buildFilterJCodesFromTableData";
import { checkIfNewDataIsForAuthReviewStatus } from "../utils/checkIfNewDataIsForAuthReviewStatus";
import {
  getCptCodesFromStore,
  removeAuthStatusChangeCommentByJCodeFromStore,
  removeAuthStatusChangeCommentsFromStore,
  removeBoCommentsFromStore,
  removeCptCodesFromStore,
  setCptCodesToStore,
  setJCodesToStore,
} from "../utils/cptUserStoreHelpers";
import { hasAnyError } from "../utils/hasTableAnyError";
import {
  getCommentTypeForStatusUpdateModal,
  getDisplayTextForStatusUpdateModal,
  getDropDownOptions,
  shouldOpenHoldModal,
  shouldOpenStatusUpdateModal,
} from "../utils/shouldOpenStatusUpdateModal";
import { updateTableDataState } from "../utils/updateTableDataState";
import { useSyncTableDataWithOrder } from "./useSyncTableDataWithOrder";

/** Delay before recomputing filtered sibling orders after table edits settle. */
const RECOMPUTE_DEBOUNCE_MS = 1000;

/**
 * Inputs required to initialize CPT table state from order context.
 */
interface UseCptTableStateParams {
  /** Route param order ID; used as the UserStore cache key for table data. */
  orderId: string | undefined;
  /** Available auth status options from Redux (drive the auth-status badge). */
  authStatusOptions: AuthStatusOptionModel[];
  /** General order details (unwrapped — Postgres `.order` payload). */
  generalOrderDetails: GeneralOrderDetails | null | undefined;
  /** Auth-on-file details (unwrapped — Postgres `.auth_on_file` payload). */
  authOnFileDetails: AuthOnFile | null | undefined;
  /**
   * Options surfaced inside each SEARCHABLE_CARD description cell. Passed
   * through to {@link generateCptCodesTableData} so the dropdown contents
   * stay configurable from the page layer.
   */
  options: SearchableCardOption[];
}

/**
 * Encapsulates all CPT table state, UserStore persistence helpers,
 * row update logic (including status/hold modal triggers), and
 * side-effect syncing (J-code writes, error tracking).
 *
 * @param params - {@link UseCptTableStateParams}; see field-level docs.
 * @returns Table state, handlers, and persistence helpers
 */
export const useCptTableState = ({
  // singleMedicalPaOrder,
  generalOrderDetails,
  authOnFileDetails,
  orderId,
  authStatusOptions,
  options,
}: UseCptTableStateParams) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: denialReasons } = useSelector(
    (state: RootState) => state.denialReasons,
  );
  const { data: queryReasons } = useSelector(
    (state: RootState) => state.queryReasons,
  );
  const facilityCategory = useFacilityCategory();

  const [tableData, setTableData] = useState<CptCodesTableRowModel[]>([]);
  const [originalTableData, setOriginalTableData] = useState<
    CptCodesTableRowModel[]
  >([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedRowData, setSelectedRowData] =
    useState<CptCodesTableRowModel | null>(null);
  const selectedRowDataForReference = useRef<{
    rowIndex: number;
    fieldKey: string;
    fieldValue: any;
  } | null>(null);

  const {
    setHasCptTableAnyError,
    hasCptTableAnyError,
    setHasCptUnsavedChanges,
  } = useBusniessOfficeNotesContext();

  const { recomputeFilteredSiblingOrders } = useDataAccumulatorForSingleOrder();

  const getCptCodes = useCallback((id: string) => getCptCodesFromStore(id), []);

  const saveTableDataToContext = useCallback(
    (data: CptCodesTableRowModel[]) => {
      try {
        setCptCodesToStore(orderId ?? "", data);
      } catch (_) {}
    },
    [orderId],
  );

  useSyncTableDataWithOrder({
    generalOrderDetails: generalOrderDetails ?? undefined,
    authOnFileDetails: authOnFileDetails ?? undefined,
    orderId,
    authStatusOptions: authStatusOptions ?? [],
    getCptCodes,
    setTableData,
    setOriginalTableData,
    setHasChanges,
    saveTableDataToContext,
    options,
    category: facilityCategory,
  });

  useEffect(() => {
    if (tableData && tableData.length > 0) {
      const jCodes = tableData.map(
        (row) => row[CptCodeWithDetailsKeys.JCodes]?.data,
      );
      setJCodesToStore(generalOrderDetails?.order_id ?? "", jCodes);
    }
  }, [tableData, generalOrderDetails?.order_id]);

  useEffect(() => {
    setHasCptTableAnyError(hasAnyError(tableData));
  }, [tableData]);

  // Publish the dirty flag to the order-scoped context so the layout footer can
  // disable "Mark as Completed" while Save Changes is pending. Reset on unmount
  // (tab switch away from CPT codes) because remounting regenerates the table
  // from order data and drops the pending edits.
  useEffect(() => {
    setHasCptUnsavedChanges(hasChanges);
    return () => setHasCptUnsavedChanges(false);
  }, [hasChanges, setHasCptUnsavedChanges]);

  // Recompute the context's filtered sibling orders from the live CPT table so
  // in-progress J-code edits (dates, auth status, remaining visits) flow into
  // the sibling-order filtering result. Debounced by 1s so rapid edits only
  // trigger a single recompute once the changes settle.
  useEffect(() => {
    if (!tableData || tableData.length === 0) {
      return;
    }
    const timeoutId = setTimeout(() => {
      const parentJCodes = buildFilterJCodesFromTableData(
        tableData,
        authStatusOptions ?? [],
      );
      recomputeFilteredSiblingOrders(parentJCodes);
    }, RECOMPUTE_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [tableData, authStatusOptions, recomputeFilteredSiblingOrders]);

  const handleStatusUpdateModal = (rowIndex: number, newValue: any): void => {
    const currentStatusId =
      tableData[rowIndex]?.[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId;
    const shouldOpen = shouldOpenStatusUpdateModal(
      currentStatusId,
      newValue?.currentStatusId,
    );
    if (!shouldOpen) return;

    const displayText = getDisplayTextForStatusUpdateModal(
      newValue?.currentStatusId,
    );
    dispatch(
      setOpenedModalId({
        id: ModalId.STATUS_UPDATE_MODAL,
        metaData: {
          options: getDropDownOptions(
            currentStatusId,
            newValue?.currentStatusId,
            queryReasons,
            denialReasons,
          ),
          label: displayText?.label,
          placeholder: displayText?.placeholder,
          commentType: getCommentTypeForStatusUpdateModal(
            newValue?.currentStatusId,
          ),
        },
      }),
    );
  };

  const handleHoldModal = (
    oldValue: PaRequired,
    newValue: PaRequired,
  ): void => {
    if (
      !shouldOpenHoldModal(oldValue?.currentStatusId, newValue?.currentStatusId)
    )
      return;
    dispatch(setOpenedModalId({ id: ModalId.STATUS_CHANGED_TO_HOLD_MODAL }));
  };

  /**
   * Snapshots the prior value of the cell at `[rowIndex][fieldKey]` into
   * `selectedRowDataForReference` (preferring the session-cached
   * `dataFromContext`, falling back to `originalTableData`) and overlays
   * the new value onto `selectedRowData` so dependent modals see the fresh
   * row state.
   *
   * Two branches in {@link handleTableRowUpdate} run this exact bookkeeping
   * before their own follow-up side effects (status/hold modals for
   * `PaRequired`; no-op for `CptCodesEditableDescription` — the
   * write-back happens later in `updateTableDataState`). Extracted so the
   * snapshot logic stays consistent between branches.
   *
   * @param fieldKey - The enum key whose prior value should be snapshotted.
   * @param rowIndex - Row being edited.
   * @param newValueForRow - Value to overlay at `spreadKey` on the row clone.
   * @param spreadKey - Property name under which `newValueForRow` is placed
   *   on the `selectedRowData` clone. Defaults to `fieldKey`; the
   *   `PaRequired` flow uses the camelCase model field (`"paRequired"`)
   *   instead so existing modal consumers keep working.
   */
  const snapshotPriorCellAndSelectRow = (
    fieldKey: string,
    rowIndex: number,
    newValueForRow: any,
    spreadKey: string = fieldKey,
  ) => {
    const dataFromContext = getCptCodes(orderId ?? "");
    const priorCellValue = dataFromContext
      ? (dataFromContext?.[rowIndex]?.[fieldKey] ?? {})
      : (originalTableData[rowIndex]?.[fieldKey] ?? {});

    selectedRowDataForReference.current = {
      fieldKey,
      rowIndex,
      fieldValue: priorCellValue,
    };
    setSelectedRowData({
      ...tableData[rowIndex],
      [spreadKey]: newValueForRow,
    });
  };

  const handleTableRowUpdate = (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => {
    let valueToUpdate = newValue;
    removeAuthStatusChangeCommentByJCodeFromStore(
      generalOrderDetails?.order_id ?? "",
      tableData?.[rowIndex]?.[CptCodeWithDetailsKeys.JCodes]?.data ?? "",
    );

    if (fieldKey === CptCodeWithDetailsKeys.PaRequired) {
      valueToUpdate = {
        text: newValue?.text,
        textColor: newValue?.textColor,
        bgColor: newValue?.bgColor,
        currentStatusId: newValue?.currentStatusId ?? "",
      };
      snapshotPriorCellAndSelectRow(
        CptCodeWithDetailsKeys.PaRequired,
        rowIndex,
        valueToUpdate,
        "paRequired",
      );
      checkIfNewDataIsForAuthReviewStatus(valueToUpdate, dispatch);
      handleStatusUpdateModal(rowIndex, valueToUpdate);
      handleHoldModal(
        selectedRowDataForReference.current?.fieldValue ?? {},
        valueToUpdate,
      );
    }

    // Snapshot the prior description cell into `selectedRowDataForReference`
    // before mutating row state so the CPT_NEW_DESCRIPTION_MODAL revert path
    // (`revertTableToLastStateOnClose`) can restore the previous value if
    // the user cancels the create-new-description confirmation.
    if (fieldKey === CptCodeWithDetailsKeys.CptCodesEditableDescription) {
      snapshotPriorCellAndSelectRow(
        CptCodeWithDetailsKeys.CptCodesEditableDescription,
        rowIndex,
        newValue,
      );
    }

    updateTableDataState(
      rowIndex,
      fieldKey,
      valueToUpdate,
      generalOrderDetails ?? ({} as GeneralOrderDetails | null | undefined),
      authOnFileDetails ?? ({} as AuthOnFile | null | undefined),
      originalTableData,
      setTableData,
      setHasChanges,
      saveTableDataToContext,
      facilityCategory,
    );
  };

  /**
   * Opens the Filing Info modal for the clicked J-code row.
   *
   * Snapshots the row (index + prior Filing Info cell) first so the modal can
   * prefill from `selectedRowData` and {@link handleFilingInfoSave} knows which
   * row to write back to.
   *
   * @param jCodeId - `filingInfo.id` of the clicked badge (the J-code id)
   */
  const openFilingInfoModal = (jCodeId: string): void => {
    const rowIndex = tableData.findIndex(
      (row) => row?.[CptCodeWithDetailsKeys.FilingInfo]?.id === jCodeId,
    );
    if (rowIndex < 0) return;

    snapshotPriorCellAndSelectRow(
      CptCodeWithDetailsKeys.FilingInfo,
      rowIndex,
      tableData[rowIndex]?.[CptCodeWithDetailsKeys.FilingInfo],
    );
    dispatch(setOpenedModalId({ id: ModalId.FILING_INFO_MODAL }));
  };

  /**
   * Writes a Filing Info modal save back to the snapshotted row so dirty
   * tracking and session persistence apply. Goes straight to
   * {@link updateTableDataState} rather than {@link handleTableRowUpdate}:
   * filing a J-code must not drop the row's pending auth-status comment.
   *
   * @param payload - Filing mode and detail emitted by the modal
   */
  const handleFilingInfoSave = (payload: FilingInfoSavePayload): void => {
    const rowIndex = selectedRowDataForReference.current?.rowIndex;
    if (rowIndex === undefined || rowIndex < 0) return;

    updateTableDataState(
      rowIndex,
      CptCodeWithDetailsKeys.FilingInfo,
      payload,
      generalOrderDetails ?? ({} as GeneralOrderDetails | null | undefined),
      authOnFileDetails ?? ({} as AuthOnFile | null | undefined),
      originalTableData,
      setTableData,
      setHasChanges,
      saveTableDataToContext,
      facilityCategory,
    );
  };

  return {
    tableData,
    setTableData,
    originalTableData,
    setOriginalTableData,
    hasChanges,
    setHasChanges,
    selectedRowData,
    selectedRowDataForReference,
    handleTableRowUpdate,
    openFilingInfoModal,
    handleFilingInfoSave,
    saveTableDataToContext,
    getCptCodes,
    hasCptTableAnyError,
    removeBoComments: removeBoCommentsFromStore,
    removeCptCodes: removeCptCodesFromStore,
    removeAuthStatusChangeComments: removeAuthStatusChangeCommentsFromStore,
  };
};
